import { cn } from '@/lib/cn'

export interface SkeletonProps {
  className?: string
  /**
   * Skjuler skelettet for skærmlæsere. Standard er `true`: en skærmlæser skal
   * høre en status-besked ("Indlæser…"), ikke et antal tomme kasser.
   */
  decorative?: boolean
}

export function Skeleton({ className, decorative = true }: SkeletonProps) {
  return (
    <div
      aria-hidden={decorative || undefined}
      className={cn('animate-shimmer rounded-md bg-overlay-strong', className)}
    />
  )
}

export interface SkeletonBlockProps {
  /** Beskrivelse til skærmlæsere, fx "Indlæser dokumenter". */
  label: string
  className?: string
  children: React.ReactNode
}

/**
 * Ramme om et sæt skeletter. Sætter `role="status"`, så hjælpeteknologi får
 * besked om at indholdet er på vej, uden at læse hvert enkelt element op.
 */
export function SkeletonBlock({ label, className, children }: SkeletonBlockProps) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  )
}

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-3.5', index === lines - 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  )
}

export function SkeletonTable({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="flex flex-col gap-px overflow-hidden rounded-[var(--radius-card)]">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 bg-overlay-subtle px-4 py-3">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-3.5', columnIndex === 0 ? 'w-1/3' : 'flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  )
}
